/* Read-only Shopify Ajax pricing bridge for the sandboxed Castlewood vanity iframe. */
(() => {
  'use strict';
  const installed = Symbol.for('castlewood.vanity.prices.bridge');
  if (window[installed]) return;
  window[installed] = true;
  const REQUEST = 'cw-vanity-prices/request';
  const RESPONSE = 'cw-vanity-prices/response';
  const SERVICE = 'castlewood-framed-cabinet-services';
  const pending = new WeakMap();
  const catalog = window.CW_VANITY_CATALOG?.catalog;
  const mappedHandles = catalog ? new Set(catalog.models.flatMap(model => Object.values(model.variants).filter(variant => variant.id && !variant.quoteOnly && !model.quoteOnly).map(variant => variant.handle || model.handle)).concat([SERVICE])) : null;
  const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
  const handleOK = value => typeof value === 'string' && value.length <= 128 &&
    (mappedHandles ? mappedHandles.has(value) : value === SERVICE || /^castlewood-(capital|altaeuro)-[a-z0-9-]+$/.test(value));
  const idOK = value => (typeof value === 'string' && /^[1-9][0-9]{0,19}$/.test(value)) ||
    (Number.isSafeInteger(value) && value > 0);
  const skuOK = value => typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9._/-]{0,127}$/.test(value);
  const frameFor = source => Array.from(document.querySelectorAll('iframe.castlewood-vanity-frame')).find(frame => frame.contentWindow === source);
  const localeRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return /^(?:\/[A-Za-z0-9-]+)*\/$/.test(root) ? root : '/';
  };
  const sanitizeProduct = (data, handle) => {
    if (!record(data) || data.handle !== handle || !Array.isArray(data.variants) || !data.variants.length || data.variants.length > 2048) throw new Error('Invalid product');
    const skus = new Set();
    const variants = data.variants.map(variant => {
      if (!record(variant) || !idOK(variant.id) || !skuOK(variant.sku) || skus.has(variant.sku) ||
          !Number.isSafeInteger(variant.price) || variant.price < 0 || typeof variant.available !== 'boolean') throw new Error('Invalid variant');
      skus.add(variant.sku);
      return {id: String(variant.id), sku: variant.sku, price: variant.price, available: variant.available};
    });
    return {handle, variants};
  };

  window.addEventListener('message', async event => {
    const data = event.data;
    if (!frameFor(event.source) || !['null', location.origin].includes(event.origin) || !record(data) || data.type !== REQUEST ||
        typeof data.requestId !== 'string' || !/^[A-Za-z0-9_-]{16,96}$/.test(data.requestId) ||
        Object.keys(data).some(key => !['type', 'requestId', 'handles'].includes(key)) ||
        !Array.isArray(data.handles) || !data.handles.length || data.handles.length > 8 ||
        !data.handles.every(handleOK) || new Set(data.handles).size !== data.handles.length) return;

    const source = event.source;
    if (pending.get(source)?.requestId === data.requestId) return;
    pending.get(source)?.controller.abort();
    const controller = new AbortController();
    const request = {controller, requestId: data.requestId};
    pending.set(source, request);
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    const root = localeRoot();
    let currency = null;
    let products = [];
    const errors = [];
    const read = async path => {
      const response = await fetch(root + path, {method: 'GET', credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: controller.signal, headers: {Accept: 'application/json'}});
      if (!response.ok) throw new Error('Unavailable');
      return response.json();
    };
    try {
      const before = await read('cart.js');
      if (!record(before) || typeof before.currency !== 'string' || !/^[A-Z]{3}$/.test(before.currency)) throw new Error('Currency unavailable');
      currency = before.currency;
      const results = await Promise.allSettled(data.handles.map(async handle => sanitizeProduct(await read(`products/${encodeURIComponent(handle)}.js`), handle)));
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') products.push(result.value);
        else errors.push(`${data.handles[index]}: price unavailable`);
      });
      const after = await read('cart.js');
      if (!record(after) || after.currency !== currency) throw new Error('Currency changed');
    } catch {
      currency = null;
      products = [];
      errors.push(controller.signal.aborted ? 'Price request timed out' : 'Current prices unavailable');
    } finally {window.clearTimeout(timeout);}
    if (pending.get(source) !== request) return;
    pending.delete(source);
    if (!frameFor(source)) return;
    // The intended iframe has an opaque origin; a wildcard is required for its
    // response. Its exact WindowProxy and the child request ID bind the exchange.
    try {source.postMessage({type: RESPONSE, requestId: data.requestId, products, currency, errors}, event.origin === 'null' ? '*' : event.origin);} catch { /* The frame may have navigated away. */ }
  });
})();

